import React, { useEffect, useRef, useState, useCallback } from "react";
import { ArrowLeft, Check, Gift, Smartphone, User as UserIcon } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../services/api";
import { isAppWebView } from "../utils/appWebView";

const getErrorMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const toTenDigitPhone = (value) => String(value || "").replace(/\D/g, "").slice(0, 10);
const OTP_EXPIRY_SECONDS = 90;

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState("login");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [referral, setReferral] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [notice, setNotice] = useState("");
  const [otpExpiresAt, setOtpExpiresAt] = useState(0);
  const [otpSecondsLeft, setOtpSecondsLeft] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [usePasswordLogin, setUsePasswordLogin] = useState(false);
  const [password, setPassword] = useState("");
  const noticeTimerRef = useRef(null);
  const digitRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const inAppWebView = isAppWebView();

  const handleDigitChange = useCallback((idx, val) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    setOtp((prev) => {
      const arr = (prev + "    ").slice(0, 4).split("");
      arr[idx] = digit;
      return arr.join("").replace(/ /g, "");
    });
    if (digit && idx < 3) {
      digitRefs[idx + 1].current?.focus();
    }
  }, []);

  const handleDigitKeyDown = useCallback((idx, e) => {
    if (e.key === "Backspace") {
      setOtp((prev) => {
        const arr = (prev + "    ").slice(0, 4).split("");
        if (arr[idx] && arr[idx].trim()) {
          arr[idx] = " ";
        } else if (idx > 0) {
          arr[idx - 1] = " ";
          digitRefs[idx - 1].current?.focus();
        }
        return arr.join("").replace(/ /g, "");
      });
      e.preventDefault();
    } else if (e.key === "ArrowLeft" && idx > 0) {
      digitRefs[idx - 1].current?.focus();
    } else if (e.key === "ArrowRight" && idx < 3) {
      digitRefs[idx + 1].current?.focus();
    }
  }, []);

  const handleDigitPaste = useCallback((e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (pasted.length > 0) {
      setOtp(pasted);
      const focusIdx = Math.min(pasted.length, 3);
      digitRefs[focusIdx].current?.focus();
    }
    e.preventDefault();
  }, []);

  const showNotice = (message) => {
    setNotice(message);
    window.clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = window.setTimeout(() => setNotice(""), 3200);
  };

  const resetFlow = (nextTab) => {
    setTab(nextTab);
    setStep(1);
    setOtp("");
    setOtpExpiresAt(0);
    setOtpSecondsLeft(0);
    setActionLoading(false);
    setNotice("");
    setUsePasswordLogin(false);
    setPassword("");
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextTab = String(params.get("tab") || "").toLowerCase() === "register" ? "register" : "login";
    const nextReferral = String(params.get("ref") || "").trim();
    setTab(nextTab);
    setStep(1);
    setOtp("");
    setOtpExpiresAt(0);
    setOtpSecondsLeft(0);
    setActionLoading(false);
    setNotice("");
    setReferral(nextReferral);
  }, [location.search]);

  useEffect(() => {
    if (!otpExpiresAt) return undefined;
    const updateRemaining = () => {
      const next = Math.max(0, Math.ceil((otpExpiresAt - Date.now()) / 1000));
      setOtpSecondsLeft(next);
    };
    updateRemaining();
    const timer = window.setInterval(updateRemaining, 250);
    return () => window.clearInterval(timer);
  }, [otpExpiresAt]);

  const sendOtp = async () => {
    if (actionLoading) return;
    const cleanPhone = toTenDigitPhone(phone);
    if (cleanPhone.length !== 10) { showNotice("Enter valid 10 digit mobile number"); return; }
    setActionLoading(true);
    try {
      const existsRes = await API.get(`/users/exists/${cleanPhone}`);
      const userExists = Boolean(existsRes.data?.exists);
      if (tab === "login" && !userExists) {
        resetFlow("register"); setPhone(cleanPhone);
        showNotice("Number not registered. Please register."); return;
      }
      if (tab === "register" && userExists) {
        resetFlow("login"); setPhone(cleanPhone);
        showNotice("Number already registered. Please login."); return;
      }
      await API.post("/send-otp", { phone: cleanPhone, mode: tab });
      setPhone(cleanPhone);
      setStep(2);
      setOtp("");
      setTimeout(() => digitRefs[0].current?.focus(), 100);
      const expiresAt = Date.now() + OTP_EXPIRY_SECONDS * 1000;
      setOtpExpiresAt(expiresAt);
      setOtpSecondsLeft(OTP_EXPIRY_SECONDS);
      showNotice("OTP sent successfully");
    } catch (err) {
      if (err?.response?.data?.code === "USER_NOT_FOUND") {
        resetFlow("register"); showNotice("New user register first"); return;
      }
      showNotice(getErrorMessage(err, "Error sending OTP"));
    } finally {
      setActionLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (actionLoading) return;
    try {
      if (step !== 2) { showNotice("Please request OTP again"); return; }
      setActionLoading(true);
      const res = await API.post("/verify-otp", { phone, otp: otp.replace(/\D/g, ""), mode: tab, name, referral });
      if (res.data.user) {
        localStorage.setItem("posm_session_active", "1");
        localStorage.setItem("user", JSON.stringify(res.data.user));
        navigate("/dashboard", { replace: true });
        return;
      }
      showNotice(res.data.message || "Login failed");
    } catch (err) {
      showNotice(getErrorMessage(err, "OTP failed"));
    } finally {
      setActionLoading(false);
    }
  };


  const loginWithPassword = async () => {
    if (actionLoading) return;
    const cleanPhone = toTenDigitPhone(phone);
    if (cleanPhone.length !== 10) { showNotice("Enter valid 10 digit mobile number"); return; }
    if (!password.trim()) { showNotice("Enter your password"); return; }
    setActionLoading(true);
    try {
      const res = await API.post("/login-with-password", { phone: cleanPhone, password: password.trim() });
      if (res.data.user) {
        localStorage.setItem("posm_session_active", "1");
        localStorage.setItem("user", JSON.stringify(res.data.user));
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      showNotice(getErrorMessage(err, "Login failed"));
    } finally {
      setActionLoading(false);
    }
  };

  const isPhoneValid = phone.length === 10;
  const isRegister = tab === "register";
  const isStepOneValid = isRegister ? isPhoneValid && String(name || "").trim().length > 0 : isPhoneValid;
  const isStepTwoValid = otp.replace(/\D/g, "").length === 4;
  const otpExpired = step === 2 && otpExpiresAt > 0 && otpSecondsLeft <= 0;
  const isPwdLoginMode = usePasswordLogin && tab === "login";
  const isActionEnabled = !actionLoading && (
    isPwdLoginMode
      ? isPhoneValid && password.trim().length > 0
      : step === 1 ? isStepOneValid : isStepTwoValid && !otpExpired
  );

  return (
    <div style={page}>
    <div style={pageInner}>
      {/* Top row */}
      <div style={topRow}>
        {!inAppWebView && (
          <button type="button" onClick={() => navigate("/", { replace: true })} style={backBtn}>
            <ArrowLeft size={20} color="#312e81" />
          </button>
        )}
        <div style={{ flex: 1 }} />
        <div style={topPhoneIcon}>
          <Smartphone size={28} color="#312e81" strokeWidth={1.6} />
        </div>
      </div>

      {/* Notice */}
      {!!notice && <div style={noticeBanner}>{notice}</div>}

      {/* Login / Register toggle */}
      <div style={toggleWrap}>
        <button
          type="button"
          style={tab === "login" ? toggleActive : toggleInactive}
          onClick={() => resetFlow("login")}
        >
          Login
        </button>
        <button
          type="button"
          style={tab === "register" ? toggleActive : toggleInactive}
          onClick={() => resetFlow("register")}
        >
          Register
        </button>
      </div>

      {/* Heading */}
      <div style={headingBlock}>
        <h1 style={heading}>{step === 2 ? "Enter OTP" : isRegister ? "Create Account" : "Enter Number"}</h1>
        <p style={subheading}>
          {step === 2
            ? `OTP sent to +91 ${phone}. Expires in ${otpSecondsLeft || OTP_EXPIRY_SECONDS}s.`
            : isRegister
            ? "Fill in your details to get started"
            : "Enter your valid mobile number"}
        </p>
      </div>

      <div style={formBox}>
        {/* Name field for register */}
        {isRegister && step === 1 && (
          <div style={fieldWrap}>
            <div style={inputRow}>
              <input
                type="text"
                placeholder="Enter your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={textInput}
              />
              <span style={inputRightIcon}><UserIcon size={20} color="#94a3b8" /></span>
            </div>
          </div>
        )}

        {/* Phone field */}
        {step === 1 && (
          <div style={fieldWrap}>
            <div style={inputRow}>
              <span style={prefixCode}>+91</span>
              <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="Enter mobile number"
                value={phone}
                onChange={(e) => setPhone(toTenDigitPhone(e.target.value))}
                style={phoneInput}
              />
              <span style={inputRightIcon}>
                {isPhoneValid
                  ? <Check size={20} color="#16a34a" />
                  : <Smartphone size={20} color="#94a3b8" />
                }
              </span>
            </div>
          </div>
        )}

        {/* Password field for password login mode */}
        {isPwdLoginMode && step === 1 && (
          <div style={fieldWrap}>
            <div style={inputRow}>
              <input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && isActionEnabled) loginWithPassword(); }}
                style={textInput}
              />
            </div>
          </div>
        )}

        {/* Referral field for register */}
        {isRegister && step === 1 && (
          <div style={fieldWrap}>
            <div style={inputRow}>
              <input
                type="text"
                placeholder="Referral code (optional)"
                value={referral}
                onChange={(e) => setReferral(e.target.value)}
                style={textInput}
              />
              <span style={inputRightIcon}><Gift size={20} color="#94a3b8" /></span>
            </div>
          </div>
        )}

        {/* OTP field */}
        {step === 2 && (
          <div style={fieldWrap}>
            <div style={otpBoxRow}>
              {[0, 1, 2, 3].map((idx) => (
                <input
                  key={idx}
                  ref={digitRefs[idx]}
                  type="text"
                  inputMode="numeric"
                  autoComplete={idx === 0 ? "one-time-code" : "off"}
                  maxLength={1}
                  value={otp[idx] || ""}
                  onChange={(e) => handleDigitChange(idx, e.target.value)}
                  onKeyDown={(e) => handleDigitKeyDown(idx, e)}
                  onPaste={handleDigitPaste}
                  onFocus={(e) => e.target.select()}
                  style={otpDigitBox}
                />
              ))}
            </div>
            {otpExpired && (
              <button
                type="button"
                style={resendBtn}
                onClick={sendOtp}
                disabled={!isStepOneValid || actionLoading}
              >
                {actionLoading ? "Sending..." : "Resend OTP"}
              </button>
            )}
          </div>
        )}

        {/* Continue / Submit button */}
        <button
          type="button"
          onClick={isPwdLoginMode ? loginWithPassword : (step === 1 ? sendOtp : verifyOtp)}
          style={isActionEnabled ? continueBtn : continueBtnDisabled}
          disabled={!isActionEnabled}
        >
          <span style={btnArrow}>→</span>{" "}
          {actionLoading
            ? isPwdLoginMode ? "Logging in..." : step === 1 ? "Sending OTP..." : "Verifying..."
            : isPwdLoginMode ? "Login" : step === 1 ? "Continue" : "Verify OTP"}
        </button>

        {/* Password / OTP toggle */}
        {tab === "login" && step === 1 && (
          <button
            type="button"
            style={pwdToggleLink}
            onClick={() => { setUsePasswordLogin((v) => !v); setPassword(""); setNotice(""); }}
          >
            {isPwdLoginMode ? "Use OTP instead" : "Login with Password"}
          </button>
        )}


      </div>

      <p style={policyText}>
        By continuing you agree to our{" "}
        <button type="button" style={policyLink} onClick={() => navigate("/terms-and-conditions")}>Terms</button>
        {" "}&amp;{" "}
        <button type="button" style={policyLink} onClick={() => navigate("/privacy-policy")}>Privacy Policy</button>
      </p>
    </div>
    </div>
  );
}

/* ── styles ── */
const PURPLE = "#312e81";

const page = {
  minHeight: "100vh",
  background: "#f3f4f7",
  display: "flex",
  justifyContent: "center",
  fontFamily: "Inter, system-ui, sans-serif",
};

const pageInner = {
  width: "100%",
  maxWidth: "440px",
  padding: "16px 20px 40px",
  boxSizing: "border-box",
  display: "grid",
  alignContent: "start",
  gap: "0",
};

const topRow = {
  display: "flex",
  alignItems: "center",
  marginBottom: "8px",
};
const backBtn = {
  width: "38px",
  height: "38px",
  border: 0,
  background: "transparent",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
};
const topPhoneIcon = {
  width: "56px",
  height: "56px",
  borderRadius: "16px",
  background: "rgba(99,102,241,0.10)",
  display: "grid",
  placeItems: "center",
};

const noticeBanner = {
  background: "#fef3c7",
  border: "1px solid #fde68a",
  color: "#92400e",
  borderRadius: "12px",
  padding: "10px 14px",
  fontWeight: 700,
  fontSize: "13px",
  marginBottom: "10px",
};

const toggleWrap = {
  display: "flex",
  background: "#e5e7eb",
  borderRadius: "999px",
  padding: "4px",
  gap: "4px",
  marginBottom: "20px",
  marginTop: "8px",
};
const toggleActive = {
  flex: 1,
  height: "38px",
  border: 0,
  borderRadius: "999px",
  background: PURPLE,
  color: "#fff",
  fontWeight: 800,
  fontSize: "14px",
  cursor: "pointer",
};
const toggleInactive = {
  flex: 1,
  height: "38px",
  border: 0,
  borderRadius: "999px",
  background: "transparent",
  color: "#6b7280",
  fontWeight: 700,
  fontSize: "14px",
  cursor: "pointer",
};

const headingBlock = {
  marginBottom: "20px",
};
const heading = {
  margin: 0,
  fontSize: "26px",
  fontWeight: 900,
  color: PURPLE,
  letterSpacing: "-0.01em",
};
const subheading = {
  margin: "5px 0 0",
  color: "#6b7280",
  fontSize: "13px",
  fontWeight: 500,
};

const formBox = {
  display: "grid",
  gap: "12px",
};

const fieldWrap = { display: "grid", gap: "6px" };
const inputRow = {
  display: "flex",
  alignItems: "center",
  background: "#fff",
  borderRadius: "14px",
  border: "1.5px solid #e5e7eb",
  overflow: "hidden",
  height: "54px",
};
const prefixCode = {
  padding: "0 12px",
  color: "#374151",
  fontWeight: 800,
  fontSize: "15px",
  borderRight: "1.5px solid #e5e7eb",
  height: "100%",
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
  background: "#f9fafb",
};
const phoneInput = {
  flex: 1,
  height: "100%",
  border: 0,
  outline: 0,
  padding: "0 12px",
  fontSize: "17px",
  color: "#111827",
  background: "transparent",
  minWidth: 0,
};
const textInput = {
  flex: 1,
  height: "100%",
  border: 0,
  outline: 0,
  padding: "0 14px",
  fontSize: "16px",
  color: "#111827",
  background: "transparent",
  minWidth: 0,
};
const inputRightIcon = {
  padding: "0 14px",
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
};

const otpBoxRow = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "12px",
};
const otpDigitBox = {
  height: "64px",
  borderRadius: "14px",
  border: `2px solid ${PURPLE}`,
  background: "#fff",
  textAlign: "center",
  fontSize: "28px",
  fontWeight: 900,
  color: PURPLE,
  outline: 0,
  boxSizing: "border-box",
  width: "100%",
};
const resendBtn = {
  marginTop: "6px",
  height: "40px",
  border: `1.5px solid ${PURPLE}`,
  borderRadius: "10px",
  background: "#fff",
  color: PURPLE,
  fontWeight: 800,
  fontSize: "13px",
  cursor: "pointer",
  width: "100%",
};

const continueBtn = {
  width: "100%",
  height: "54px",
  border: 0,
  borderRadius: "14px",
  background: PURPLE,
  color: "#fff",
  fontSize: "17px",
  fontWeight: 800,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  boxShadow: "0 8px 24px rgba(49,46,129,0.28)",
  marginTop: "4px",
};
const continueBtnDisabled = {
  ...continueBtn,
  background: "#c7d2fe",
  color: "#e0e7ff",
  boxShadow: "none",
  cursor: "not-allowed",
};
const btnArrow = { fontSize: "18px", fontWeight: 900 };

const orDivider = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginTop: "2px",
};
const orLine = {
  flex: 1,
  height: "1px",
  background: "#e5e7eb",
  display: "block",
};
const orText = {
  color: "#9ca3af",
  fontSize: "13px",
  fontWeight: 700,
  flexShrink: 0,
};

const codeLinkBtn = {
  border: 0,
  background: "transparent",
  color: "#6366f1",
  fontWeight: 800,
  fontSize: "13px",
  cursor: "pointer",
  textDecoration: "underline",
  padding: "4px 0",
  textAlign: "center",
};
const codeBox = {
  background: "#fff",
  border: "1.5px solid #e0e7ff",
  borderRadius: "14px",
  padding: "14px",
  display: "grid",
  gap: "10px",
};
const codeBoxHint = { margin: 0, fontSize: "12px", color: "#6b7280", fontWeight: 600 };
const codeRow = { display: "grid", gridTemplateColumns: "1fr 80px", gap: "8px" };
const pwdRow = { display: "grid", gridTemplateColumns: "1fr 80px", gap: "8px" };
const pwdInputWrap = { position: "relative", display: "flex", alignItems: "center" };
const eyeBtn = { position: "absolute", right: "10px", border: 0, background: "transparent", cursor: "pointer", display: "grid", placeItems: "center", padding: 0 };
const codeInput = {
  height: "44px",
  borderRadius: "10px",
  border: "1.5px solid #e5e7eb",
  background: "#f9fafb",
  fontSize: "18px",
  fontWeight: 900,
  letterSpacing: "0.2em",
  textAlign: "center",
  color: PURPLE,
  outline: 0,
  minWidth: 0,
  boxSizing: "border-box",
};
const codeLoginBtn = {
  height: "44px",
  border: 0,
  borderRadius: "10px",
  background: PURPLE,
  color: "#fff",
  fontWeight: 800,
  fontSize: "13px",
  cursor: "pointer",
};
const codeLoginBtnOff = { ...codeLoginBtn, background: "#c7d2fe", color: "#e0e7ff", cursor: "not-allowed" };
const codeHideBtn = {
  border: 0,
  background: "transparent",
  color: "#9ca3af",
  fontSize: "11px",
  fontWeight: 700,
  cursor: "pointer",
  textAlign: "right",
  padding: 0,
};

const policyText = {
  marginTop: "24px",
  textAlign: "center",
  color: "#9ca3af",
  fontSize: "12px",
};
const policyLink = {
  border: 0,
  background: "transparent",
  color: PURPLE,
  fontWeight: 700,
  fontSize: "12px",
  cursor: "pointer",
  padding: 0,
};


const pwdToggleLink = {
  background: "none",
  border: "none",
  color: PURPLE,
  fontSize: "13px",
  fontWeight: 700,
  cursor: "pointer",
  textDecoration: "underline",
  padding: "2px 0",
  textAlign: "center",
  width: "100%",
};

export default Login;
